import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TablePagination,
  Button,
  Stack,
  TextField,
  InputAdornment,
  Grid,
  IconButton,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/formatters';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AddIcon from '@mui/icons-material/Add';
import PDFViewer from '../shared/PDFViewer';
import { getFileNameFromContentDisposition } from '../../utils/contentDisposition';

interface MovimientoVario {
  nroFormulario?: number;
  nroTipoFormulario?: string;
  nroSuc?: number;
  fechaEmision?: string;
  codigoCaja?: number;
  usuario?: string;
  nombre?: string;
  descripcion?: string;
  importeTotal?: number;
  letra?: string;
  nroComprobante?: number;
  tipoPago?: string;
  ingresoEgreso?: string;
  detalle?: string;
  // Campos adicionales que pueden venir
  medioDeCobro?: string;
  medioCobro?: string;
  mcp?: string;
  medios?: Array<{
    ingresoEgreso?: string;
    [key: string]: any;
  }>;
  fecha?: string;
  comprobante?: string;
  tipo?: string;
  tipoComprobante?: string;
  cartera?: string;
  debe?: number;
  haber?: number;
  saldo?: number;
  [key: string]: any;
}


const MovimientosVarios: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [currentTab, setCurrentTab] = useState(0); // 0: TODOS, 1: INGRESOS, 2: EGRESOS
  const [movimientos, setMovimientos] = useState<MovimientoVario[]>([]);
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState<string>('fechaEmision');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const getFirstDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  };
  const getLastDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0);
  };
  const [fechaDesde, setFechaDesde] = useState<string>(() => {
    return getFirstDayOfMonth().toISOString().split('T')[0];
  });
  const [fechaHasta, setFechaHasta] = useState<string>(() => {
    return getLastDayOfMonth().toISOString().split('T')[0];
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [selectedMovimientoId, setSelectedMovimientoId] = useState<number | null>(null);
  const [pdfFileName, setPdfFileName] = useState<string | null>(null);

  /** Convierte fecha del formulario (YYYY-MM-DD) al formato del API (MM-DD-YYYY) */
  const toApiDate = (isoDate: string): string => {
    if (!isoDate) return '';
    const [y, m, d] = isoDate.split('-');
    return `${m}-${d}-${y}`;
  };

  const cargarMovimientos = async () => {
    setLoading(true);
    try {
      const tipoMovimiento = currentTab === 0 ? undefined : currentTab === 1 ? '5' : '7';
      const params: Record<string, string | number | undefined> = {
        FechaDesde: toApiDate(fechaDesde),
        FechaHasta: toApiDate(fechaHasta),
        PageNumber: page + 1,
        PageSize: Math.min(rowsPerPage, 50), // Backend: máximo 50
      };
      if (tipoMovimiento != null) {
        params.TipoMovimiento = tipoMovimiento;
      }

      const response = await api.get<any>('/api/TesoreriaCaja/movimientos-varios', { params });

      const data = response.data;
      let items: MovimientoVario[] = [];
      let totalCount = 0;

      if (Array.isArray(data)) {
        items = data;
        totalCount = data.length;
      } else if (data && Array.isArray(data.items)) {
        items = data.items || [];
        totalCount = data.totalCount ?? items.length;
      } else if (data && data.data && Array.isArray(data.data)) {
        items = data.data || [];
        totalCount = data.totalCount ?? items.length;
      }

      setMovimientos(items);
      setTotalRecords(totalCount);
    } catch (error: any) {
      console.error('Error al cargar movimientos:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Error al cargar los movimientos varios',
        { variant: 'error' }
      );
      setMovimientos([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarMovimientos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, fechaDesde, fechaHasta, currentTab]);

  const handleNuevoMovimiento = () => {
    navigate('/tesoreria/movimientos-varios/nuevo');
  };

  // Filtro por búsqueda en la página actual (fecha y tipo ya vienen del API)
  const movimientosConFiltros = useMemo(() => {
    if (!searchTerm.trim()) return movimientos;
    const term = searchTerm.trim().toLowerCase();
    return movimientos.filter((m) => {
      const nombre = (m.nombre || '').toLowerCase();
      const descripcion = (m.descripcion || '').toLowerCase();
      const nroComprobante = String(m.nroComprobante ?? '').toLowerCase();
      const nroFormulario = String(m.nroFormulario ?? '').toLowerCase();
      return (
        nombre.includes(term) ||
        descripcion.includes(term) ||
        nroComprobante.includes(term) ||
        nroFormulario.includes(term)
      );
    });
  }, [movimientos, searchTerm]);

  // Los datos ya vienen filtrados por tab del API (TipoMovimiento)
  const movimientosFiltrados = movimientosConFiltros;

  // Ordenar movimientos
  const movimientosOrdenados = useMemo(() => {
    const sorted = [...movimientosFiltrados];
    sorted.sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      if (sortField === 'fechaEmision' || sortField === 'fecha') {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = (bValue || '').toLowerCase();
      } else if (typeof aValue === 'number') {
        // Mantener como número para ordenamiento numérico
      } else {
        aValue = aValue || 0;
        bValue = bValue || 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [movimientosFiltrados, sortField, sortDirection]);

  // Preparar movimientos con los campos necesarios
  const movimientosConSaldo = useMemo(() => {
    return movimientosOrdenados.map(movimiento => {
      return {
        ...movimiento,
        // Mantener importeTotal para mostrar en la columna Importe
      };
    });
  }, [movimientosOrdenados]);

  // El API ya devuelve la página actual; la búsqueda se aplica en cliente sobre esa página
  const movimientosPaginados = movimientosConSaldo;

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    setPage(0); // Resetear a la primera página al cambiar de tab
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleImprimirPDF = async (id: number) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        enqueueSnackbar('No hay token de autenticación', { variant: 'error' });
        return;
      }
      const response = await api.get(
        `/api/Tesoreria/movimientos-varios/${id}/pdf`,
        { responseType: 'blob' }
      );
      const cd = (response.headers as any)?.['content-disposition'] as string | undefined;
      const backendFileName = getFileNameFromContentDisposition(cd);
      setPdfFileName(backendFileName);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      setPdfUrl(url);
      setSelectedMovimientoId(id);
    } catch (error) {
      enqueueSnackbar('Error al generar el PDF del movimiento', { variant: 'error' });
    }
  };

  const handleClosePDF = () => {
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
    }
    setPdfUrl(null);
    setSelectedMovimientoId(null);
    setPdfFileName(null);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: '#1976d2'
              }}
            >
              Movimientos Varios
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleNuevoMovimiento}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
              }}
            >
              Nuevo Movimiento
            </Button>
          </Stack>

          {/* Campo de búsqueda y filtros */}
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Buscar por nombre, descripción o número..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      }
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#666' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Fecha Desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => {
                    setFechaDesde(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Fecha Hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => {
                    setFechaHasta(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={currentTab} onChange={handleTabChange}>
              <Tab 
                label={currentTab === 0 ? `TODOS ${totalRecords}` : 'TODOS'}
                sx={{
                  textTransform: 'none',
                  fontWeight: currentTab === 0 ? 600 : 400,
                  color: currentTab === 0 ? '#1976d2' : 'inherit',
                  fontSize: '0.875rem',
                }}
              />
              <Tab 
                label={currentTab === 1 ? `INGRESOS ${totalRecords}` : 'INGRESOS'}
                icon={<ArrowUpwardIcon sx={{ color: '#4caf50', fontSize: '1.2rem' }} />}
                iconPosition="start"
                sx={{
                  textTransform: 'none',
                  fontWeight: currentTab === 1 ? 600 : 400,
                  color: currentTab === 1 ? '#1976d2' : 'inherit',
                  fontSize: '0.875rem',
                }}
              />
              <Tab 
                label={currentTab === 2 ? `EGRESOS ${totalRecords}` : 'EGRESOS'}
                icon={<ArrowDownwardIcon sx={{ color: '#f44336', fontSize: '1.2rem' }} />}
                iconPosition="start"
                sx={{
                  textTransform: 'none',
                  fontWeight: currentTab === 2 ? 600 : 400,
                  color: currentTab === 2 ? '#1976d2' : 'inherit',
                  fontSize: '0.875rem',
                }}
              />
            </Tabs>
          </Box>

          {/* Tabla */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: 2,
                  boxShadow: 'none',
                  border: '1px solid #e0e0e0',
                }}
              >
                <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>MOTIVO</TableCell>
                    <TableCell 
                      sx={{ fontWeight: 600, cursor: 'pointer' }}
                      onClick={() => handleSort('fechaEmision')}
                    >
                      FECHA {sortField === 'fechaEmision' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>NRO COMPROBANTE</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>TIPO</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>DESCRIPCION</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>IMPORTE</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>ACCIONES</TableCell>
                  </TableRow>
                </TableHead>
                  <TableBody>
                    {movimientosPaginados.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No hay movimientos para mostrar
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                    movimientosPaginados.map((movimiento, index) => {
                      // Mapear campos según la estructura del API
                      const nroTipoFormulario = String((movimiento.nroTipoFormulario || '').trim());
                      const esEgreso = nroTipoFormulario === '7';
                      const esIngreso = nroTipoFormulario === '5';
                      
                      // MOTIVO - usar el campo "nombre" del API
                      const nombre = movimiento.nombre ? movimiento.nombre.trim() : '-';
                      
                      // FECHA - usar fechaEmision del API
                      const fecha = movimiento.fechaEmision || '';
                      
                      // NRO COMPROBANTE - mostrar el número completo como int
                      const nroComprobante = movimiento.nroComprobante !== undefined && movimiento.nroComprobante !== null
                        ? movimiento.nroComprobante
                        : null;
                      
                      // TIPO - usar el campo "nroTipoFormulario"
                      // Si nroTipoFormulario es "5" -> "Ingreso", si es "7" -> "Egreso"
                      let tipo = '-';
                      const tipoForm = String((movimiento.nroTipoFormulario || '').trim());
                      if (tipoForm === '5') {
                        tipo = 'Ingreso';
                      } else if (tipoForm === '7') {
                        tipo = 'Egreso';
                      }
                      
                      // DESCRIPCION - usar el campo "descripcion" del API
                      const descripcion = movimiento.descripcion ? movimiento.descripcion.trim() : '-';
                      
                      // IMPORTE - usar importeTotal del API
                      const importe = movimiento.importeTotal || 0;
                      
                      // Determinar colores de fondo según el tipo de formulario
                      // INGRESOS (nroTipoFormulario === "5"): verde
                      // EGRESOS (nroTipoFormulario === "7"): rojo
                      const backgroundColor = esIngreso 
                        ? '#e8f5e9' // Verde claro para ingresos
                        : esEgreso
                        ? '#ffebee' // Rojo claro para egresos
                        : '#fff'; // Blanco por defecto
                      
                      return (
                        <TableRow
                          key={index}
                          sx={{
                            backgroundColor,
                            '&:hover': { 
                              backgroundColor: esIngreso 
                                ? '#c8e6c9' // Verde más oscuro al hover para ingresos
                                : esEgreso
                                ? '#ffcdd2' // Rojo más oscuro al hover para egresos
                                : '#f5f5f5'
                            }
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {esIngreso && (
                                <ArrowUpwardIcon sx={{ color: '#4caf50', fontSize: 16 }} />
                              )}
                              {esEgreso && (
                                <ArrowDownwardIcon sx={{ color: '#f44336', fontSize: 16 }} />
                              )}
                              {nombre}
                            </Box>
                          </TableCell>
                          <TableCell>{fecha ? formatDate(fecha) : '-'}</TableCell>
                          <TableCell>{nroComprobante !== null ? nroComprobante : '-'}</TableCell>
                          <TableCell>{tipo}</TableCell>
                          <TableCell>{descripcion}</TableCell>
                          <TableCell align="right">
                            {importe > 0 ? formatCurrency(importe) : '-'}
                          </TableCell>
                          <TableCell align="center">
                            {movimiento.nroFormulario != null && (
                              <Tooltip title="Imprimir PDF">
                                <IconButton
                                  onClick={() => handleImprimirPDF(movimiento.nroFormulario!)}
                                  size="small"
                                  sx={{
                                    bgcolor: '#2196f3',
                                    color: '#fff',
                                    borderRadius: 1.5,
                                    '&:hover': { bgcolor: '#1976d2' }
                                  }}
                                >
                                  <PrintIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={totalRecords}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50]}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </>
          )}
        </CardContent>
      </Card>

      {pdfUrl && selectedMovimientoId != null && (
        <PDFViewer
          pdfUrl={pdfUrl}
          onClose={handleClosePDF}
          title={`Movimiento ${selectedMovimientoId}`}
          documentId={`${selectedMovimientoId}`}
          downloadFileName={pdfFileName ?? undefined}
        />
      )}
    </Box>
  );
};

export default MovimientosVarios;
